import { createElementId, getUnixTimestampUTC } from "../util/utils";
import { Point } from "./schemas/canvas-element";
import { MondayTaskElement } from "./schemas/monday-task";
import consts from "shared/consts";

// Monday Task Section Heights
// These constants define the height of each section in the Monday task card

/** Base section height - includes header (logo + breadcrumbs) and item name */
export const MONDAY_TASK_BASE_HEIGHT = 80;

/** Pinned columns section height - displayed when there are pinned columns */
export const MONDAY_TASK_PINNED_COLUMNS_HEIGHT = 40;

/** Image section height - displayed when there's a cover image */
export const MONDAY_TASK_IMAGE_HEIGHT = 184;

/** Padding for dynamic sections */
export const MONDAY_TASK_DYNAMIC_SECTION_PADDING = 22.5;

/** Column details section - height per column row */
export const MONDAY_TASK_COLUMN_DETAILS_ROW_HEIGHT = 47.5;

/** Updates section - height per update row */
export const MONDAY_TASK_UPDATES_ROW_HEIGHT = 55;

/** Updates section - header height */
export const MONDAY_TASK_UPDATES_HEADER_HEIGHT = 30;

/** Updates section - show more button height */
export const MONDAY_TASK_UPDATES_SHOW_MORE_HEIGHT = 20;

/** Files section - height per file row */
export const MONDAY_TASK_FILES_ROW_HEIGHT = 30;

/** Default width for Monday task cards */
export const MONDAY_TASK_WIDTH = 275;

export enum MondayTaskSection {
  BASE = "base",
  PINNED_COLUMNS = "pinnedColumns",
  IMAGE = "image",
  COLUMN_DETAILS = "columnDetails",
  FILES = "files",
  UPDATES = "updates",
}

export const MONDAY_TASK_SECTION_HEIGHTS: Record<MondayTaskSection, number> = {
  [MondayTaskSection.BASE]: MONDAY_TASK_BASE_HEIGHT,
  [MondayTaskSection.PINNED_COLUMNS]: MONDAY_TASK_PINNED_COLUMNS_HEIGHT,
  [MondayTaskSection.IMAGE]: MONDAY_TASK_IMAGE_HEIGHT,
  [MondayTaskSection.COLUMN_DETAILS]: 0, // Dynamic height, calculated based on number of columns
  [MondayTaskSection.FILES]: 0, // Dynamic height, calculated based on number of files
  [MondayTaskSection.UPDATES]: 0, // Dynamic height, calculated based on number of updates
};

/**
 * Calculate the total height for a Monday task card based on visible sections
 * @param sections Array of sections to include in the height calculation
 * @param sectionHeights Optional custom heights for specific sections (e.g., for dynamic COLUMN_DETAILS height)
 * @returns Total height in pixels
 */
export function calculateMondayTaskHeight(
  sections: MondayTaskSection[],
  sectionHeights?: Partial<Record<MondayTaskSection, number>>
): number {
  return sections.reduce((total, section) => {
    const height = sectionHeights?.[section] ?? MONDAY_TASK_SECTION_HEIGHTS[section];
    return total + height;
  }, 0);
}

export type MondayTaskInitOptions = {
  mondayItemId: string;
  integrationId: string;
  fill?: string;
  baseOnElement?: any;
};

export function placeMondayTask(point: Point, init: MondayTaskInitOptions) {
  const { mondayItemId, integrationId, fill, baseOnElement } = init;
  const id = createElementId();
  const fillColor = fill || (baseOnElement as any)?.color || (baseOnElement as any)?.fill || "#EBEDF3";

  // For Gantt compatibility: preserve fromDate/toDate in fieldValues
  const fromDate = (baseOnElement as any)?.fromDate;
  const toDate = (baseOnElement as any)?.toDate;

  return {
    id,
    mondayTask: {
      ...(baseOnElement ?? {}),
      type: consts.CANVAS_ELEMENTS.MONDAY_TASK,
      x: point.x,
      y: point.y,
      scaleX: 1,
      scaleY: 1,
      width: MONDAY_TASK_WIDTH,
      height: MONDAY_TASK_SECTION_HEIGHTS[MondayTaskSection.BASE],
      fill: fillColor,
      zIndexLastChangeTime: getUnixTimestampUTC(),
      mondayItemId,
      integrationId,
      fieldValues: {
        ...(baseOnElement?.fieldValues ?? {}),
        ...(fromDate !== undefined && { fromDate }),
        ...(toDate !== undefined && { toDate }),
      },
      flags: 0,
    } as MondayTaskElement,
  };
}
